import { defineStore } from 'pinia'
import { inject, ref, computed } from 'vue'
import { getApp } from 'firebase/app'
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import { RequestService } from '../services/RequestService'
import { AdminCenterService } from '../services/AdminCenterService'
import { roleKeysForAccountPermission, ROLE_KEYS } from '../access/roleKeys'
import { getAccountOwners } from '../utils/accountOwnersHelpers'

const { handleErrors } = RequestService()

export const useAdminCenterStore = defineStore('adminCenter', () => {
  const functionsBaseUrl = inject('functionsBaseUrl')

  // Instantiate service once during setup so inject() resolves correctly
  const adminCenterService = AdminCenterService()

  // Agency ID resolved directly from Firestore
  const agencyId = ref(null)

  // Unified loading flag for all Admin Center data
  const isLoading = ref(false)

  // Stats
  const totalEmployers = ref(0)
  const unassignedEmployers = ref(0)
  const totalAccountOwners = ref(0)

  // Pagination
  const PAGE_SIZE = 10

  // Users lists (populated from backend endpoint)
  const activeUsers = ref([])
  const pendingUsers = ref([])
  // Full (unpaginated) active users list for employer assignment dropdowns
  const allActiveUsers = ref([])

  // Total counts from backend (for pagination)
  const totalActive = ref(0)
  const totalPending = ref(0)

  // Loading flags per tab
  const isLoadingMoreActive = ref(false)
  const isLoadingMorePending = ref(false)

  // Computed stats derived from total counts
  const invitesSent = computed(() => totalActive.value + totalPending.value)
  const pendingInvites = computed(() => totalPending.value)

  const hasMoreActive = computed(() => activeUsers.value.length < totalActive.value)
  const hasMorePending = computed(() => pendingUsers.value.length < totalPending.value)

  // Employer assignments state
  const allEmployers = ref([])
  const unassignedEmployersList = ref([])
  const totalAllEmployers = ref(0)
  const totalUnassignedEmployers = ref(0)
  const isLoadingEmployers = ref(false)
  const isLoadingMoreAllEmployers = ref(false)
  const isLoadingMoreUnassignedEmployers = ref(false)

  const hasMoreAllEmployers = computed(() => allEmployers.value.length < totalAllEmployers.value)
  const hasMoreUnassignedEmployers = computed(() => unassignedEmployersList.value.length < totalUnassignedEmployers.value)

  /**
   * Resolves the agency_id for the current authenticated user
   * by querying the 'users' collection directly with the user's UID.
   * This makes the AdminCenter self-contained and independent of HomePage's load order.
   */
  async function resolveAgencyId() {
    if (agencyId.value) return agencyId.value

    const uid = localStorage.getItem('userUid')
    if (!uid) {
      handleErrors('AdminCenterStore.resolveAgencyId', new Error('No userUid in localStorage'))
      return null
    }

    try {
      const db = getFirestore(getApp())
      const userDoc = await getDoc(doc(db, 'users', uid))

      if (!userDoc.exists()) {
        handleErrors('AdminCenterStore.resolveAgencyId', new Error('User document not found'))
        return null
      }

      const userData = userDoc.data()
      agencyId.value = userData.agency_id || null

      if (!agencyId.value) {
        handleErrors('AdminCenterStore.resolveAgencyId', new Error('User has no agency_id'))
      }

      return agencyId.value
    } catch (error) {
      handleErrors('AdminCenterStore.resolveAgencyId', error)
      return null
    }
  }

  async function loadAgencyStats() {
    const currentAgencyId = await resolveAgencyId()
    if (!currentAgencyId) return

    try {
      const db = getFirestore(getApp())

      // Query all employers for this agency
      const employersQuery = query(
        collection(db, 'employers'),
        where('agency_id', '==', String(currentAgencyId))
      )
      const employersSnapshot = await getDocs(employersQuery)

      totalEmployers.value = employersSnapshot.size

      // Count unassigned employers (no account owners at all)
      let unassignedCount = 0
      employersSnapshot.docs.forEach(docSnap => {
        if (getAccountOwners(docSnap.data()).length === 0) {
          unassignedCount++
        }
      })
      unassignedEmployers.value = unassignedCount

    } catch (error) {
      handleErrors('AdminCenterStore.loadAgencyStats', error)
    }
  }

  /**
   * Fetches both user lists and applies them to state.
   * @param {number} activeLimit - Number of active users to fetch
   * @param {number} pendingLimit - Number of pending users to fetch
   */
  async function _fetchAndApplyUsers(activeLimit = PAGE_SIZE, pendingLimit = PAGE_SIZE) {
    const [activeData, pendingData, allActiveData] = await Promise.all([
      adminCenterService.getAgencyMembers({ type: 'active', limit: activeLimit, offset: 0 }),
      adminCenterService.getAgencyMembers({ type: 'pending', limit: pendingLimit, offset: 0 }),
      adminCenterService.getAgencyMembers({ type: 'active' })
    ])
    activeUsers.value = (activeData.activeUsers || []).map(u => ({ ...u, status: 'Active' }))
    totalActive.value = activeData.totalActive || 0
    totalAccountOwners.value = activeData.totalAccountOwners || 0
    pendingUsers.value = (pendingData.pendingUsers || []).map(u => ({ ...u, status: 'Pending' }))
    totalPending.value = pendingData.totalPending || 0
    allActiveUsers.value = (allActiveData.activeUsers || []).map(u => ({ ...u, status: 'Active' }))
  }

  /**
   * Loads only the first page of pending users from the backend.
   * Used after createUser and cancelInvite where only pending list changes.
   */
  async function _reloadPendingUsers() {
    const data = await adminCenterService.getAgencyMembers({
      type: 'pending',
      limit: PAGE_SIZE,
      offset: 0
    })
    pendingUsers.value = (data.pendingUsers || []).map(u => ({ ...u, status: 'Pending' }))
    totalActive.value = data.totalActive || 0
    totalPending.value = data.totalPending || 0
    totalAccountOwners.value = data.totalAccountOwners || 0
  }

  /**
   * Loads the next page of active users and appends to the existing list.
   */
  async function loadMoreActiveUsers() {
    if (!hasMoreActive.value || isLoadingMoreActive.value) return
    isLoadingMoreActive.value = true
    try {
      const data = await adminCenterService.getAgencyMembers({
        type: 'active',
        limit: PAGE_SIZE,
        offset: activeUsers.value.length
      })
      const newUsers = (data.activeUsers || []).map(u => ({ ...u, status: 'Active' }))
      activeUsers.value = [...activeUsers.value, ...newUsers]
      totalActive.value = data.totalActive || totalActive.value
    } catch (error) {
      handleErrors('AdminCenterStore.loadMoreActiveUsers', error)
    } finally {
      isLoadingMoreActive.value = false
    }
  }

  /**
   * Loads the next page of pending users and appends to the existing list.
   */
  async function loadMorePendingUsers() {
    if (!hasMorePending.value || isLoadingMorePending.value) return
    isLoadingMorePending.value = true
    try {
      const data = await adminCenterService.getAgencyMembers({
        type: 'pending',
        limit: PAGE_SIZE,
        offset: pendingUsers.value.length
      })
      const newUsers = (data.pendingUsers || []).map(u => ({ ...u, status: 'Pending' }))
      pendingUsers.value = [...pendingUsers.value, ...newUsers]
      totalPending.value = data.totalPending || totalPending.value
    } catch (error) {
      handleErrors('AdminCenterStore.loadMorePendingUsers', error)
    } finally {
      isLoadingMorePending.value = false
    }
  }

  /**
   * Loads all Admin Center data in parallel (employer stats + first page of each user type).
   * Both cards show loading simultaneously and finish together.
   */
  async function loadAll() {
    isLoading.value = true
    try {
      await Promise.all([loadAgencyStats(), _fetchAndApplyUsers()])
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Creates a single user via the AdminCenterService.
   * @param {Object} formData - { firstName, lastName, title, email, accountPermissions }
   * @returns {Object} result from backend { success, email, status?, error? }
   */
  async function createUser(formData) {
    const currentAgencyId = await resolveAgencyId()
    if (!currentAgencyId) {
      throw new Error('Unable to resolve agency. Please try again.')
    }

    const result = await adminCenterService.createUser({
      firstName: formData.firstName,
      lastName: formData.lastName,
      title: formData.title,
      email: formData.email,
      agencyId: currentAgencyId,
      roleKeys: roleKeysForAccountPermission(formData.accountPermissions)
    })

    // Reload first page of pending users to reflect the new user
    await _reloadPendingUsers()
    return result
  }

  /**
   * Appends the agencyAdmin role key to a user.
   * @param {string} uid - User ID
   * @returns {Promise<Object>}
   */
  async function grantAgencyAdmin(uid) {
    const result = await adminCenterService.grantUserRoleKey(uid, ROLE_KEYS.AGENCY_ADMIN)
    const user = activeUsers.value.find(u => u.id === uid)
    if (user && !user.roleKeys?.includes(ROLE_KEYS.AGENCY_ADMIN)) {
      user.roleKeys = [...(user.roleKeys || []), ROLE_KEYS.AGENCY_ADMIN]
    }
    return result
  }

  /**
   * Deactivates a user and optionally reassigns their employers.
   * @param {string} uid - User ID
   * @param {string|null} reassignTo - Email of new owner, or null to unassign
   * @returns {Promise<Object>}
   */
  async function deactivateUser(uid, reassignTo = null) {
    const result = await adminCenterService.deactivateUser(uid, reassignTo)
    // Reload both lists to reflect changes
    await _fetchAndApplyUsers()
    return result
  }

  /**
   * Resends the password creation invite email to a pending user.
   * @param {string} uid - User ID of the pending user
   * @returns {Promise<Object>}
   */
  async function resendInvite(uid) {
    const result = await adminCenterService.resendInvite(uid)
    // Reload pending users to reflect the updated lastInviteSentAt
    await _reloadPendingUsers()
    return result
  }

  /**
   * Cancels a pending invite by deleting the user entirely.
   * @param {string} uid - User ID of the pending user
   * @returns {Promise<Object>}
   */
  async function cancelInvite(uid) {
    const result = await adminCenterService.cancelInvite(uid)
    // Reload pending users to reflect the removal
    await _reloadPendingUsers()
    return result
  }

  // ── Employer assignments ──

  /**
   * Fetches both employer lists and applies them to state.
   * @param {number} allLimit - Number of "all" employers to fetch
   * @param {number} unassignedLimit - Number of unassigned employers to fetch
   */
  async function _fetchAndApplyEmployers(allLimit = PAGE_SIZE, unassignedLimit = PAGE_SIZE) {
    const [allData, unassignedData] = await Promise.all([
      adminCenterService.getAgencyEmployers({ type: 'all', limit: allLimit, offset: 0 }),
      adminCenterService.getAgencyEmployers({ type: 'unassigned', limit: unassignedLimit, offset: 0 })
    ])
    allEmployers.value = allData.employers || []
    totalAllEmployers.value = allData.totalAll || 0
    totalUnassignedEmployers.value = allData.totalUnassigned || 0
    unassignedEmployersList.value = unassignedData.employers || []
  }

  async function loadMoreAllEmployers() {
    if (!hasMoreAllEmployers.value || isLoadingMoreAllEmployers.value) return
    isLoadingMoreAllEmployers.value = true
    try {
      const data = await adminCenterService.getAgencyEmployers({
        type: 'all',
        limit: PAGE_SIZE,
        offset: allEmployers.value.length
      })
      allEmployers.value = [...allEmployers.value, ...(data.employers || [])]
      totalAllEmployers.value = data.totalAll || totalAllEmployers.value
    } catch (error) {
      handleErrors('AdminCenterStore.loadMoreAllEmployers', error)
    } finally {
      isLoadingMoreAllEmployers.value = false
    }
  }

  async function loadMoreUnassignedEmployers() {
    if (!hasMoreUnassignedEmployers.value || isLoadingMoreUnassignedEmployers.value) return
    isLoadingMoreUnassignedEmployers.value = true
    try {
      const data = await adminCenterService.getAgencyEmployers({
        type: 'unassigned',
        limit: PAGE_SIZE,
        offset: unassignedEmployersList.value.length
      })
      unassignedEmployersList.value = [...unassignedEmployersList.value, ...(data.employers || [])]
      totalUnassignedEmployers.value = data.totalUnassigned || totalUnassignedEmployers.value
    } catch (error) {
      handleErrors('AdminCenterStore.loadMoreUnassignedEmployers', error)
    } finally {
      isLoadingMoreUnassignedEmployers.value = false
    }
  }

  async function loadEmployerAssignments() {
    isLoadingEmployers.value = true
    try {
      await _fetchAndApplyEmployers()
    } finally {
      isLoadingEmployers.value = false
    }
  }

  /**
   * Submits batch account-owner changes and reloads all data preserving pagination.
   * @param {Array} assignments - [{ employerId, accountOwners, accountOwner }]
   */
  async function submitAssignments(assignments) {
    const result = await adminCenterService.updateEmployerAssignments(assignments)

    await Promise.all([
      _fetchAndApplyUsers(_currentActiveLimit(), _currentPendingLimit()),
      _fetchAndApplyEmployers(_currentAllLimit(), _currentUnassignedLimit()),
      loadAgencyStats()
    ])

    return result
  }

  /**
   * Refreshes users data preserving current pagination state.
   */
  async function refreshUsers() {
    isLoading.value = true
    try {
      await Promise.all([
        _fetchAndApplyUsers(_currentActiveLimit(), _currentPendingLimit()),
        loadAgencyStats()
      ])
    } catch (error) {
      handleErrors('AdminCenterStore.refreshUsers', error)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Refreshes employer assignments data preserving current pagination state.
   */
  async function refreshEmployers() {
    isLoadingEmployers.value = true
    try {
      await Promise.all([
        _fetchAndApplyEmployers(_currentAllLimit(), _currentUnassignedLimit()),
        loadAgencyStats()
      ])
    } catch (error) {
      handleErrors('AdminCenterStore.refreshEmployers', error)
    } finally {
      isLoadingEmployers.value = false
    }
  }

  // ── Pagination-preserving limit helpers ──
  function _currentActiveLimit() { return Math.max(activeUsers.value.length, PAGE_SIZE) }
  function _currentPendingLimit() { return Math.max(pendingUsers.value.length, PAGE_SIZE) }
  function _currentAllLimit() { return Math.max(allEmployers.value.length, PAGE_SIZE) }
  function _currentUnassignedLimit() { return Math.max(unassignedEmployersList.value.length, PAGE_SIZE) }

  // ── Transform certification progress ──
  // Loaded once per visit: the gateway returns every learner of the agency unsorted, and
  // TransformCertificationProgress.vue sorts and batches them client-side.
  const certificationProgressRows = ref([])
  const isLoadingCertificationProgress = ref(false)
  const hasLoadedCertificationProgress = ref(false)
  const certificationProgressError = ref('')
  // resetAll (logout) can run while a load is still pending. Each load takes a sequence
  // number and only the latest may touch state, so a slow response for the previous
  // session never repopulates the table for the next one.
  let certificationProgressRequestSeq = 0

  async function loadCertificationProgress() {
    if (isLoadingCertificationProgress.value) return
    certificationProgressRequestSeq += 1
    const requestId = certificationProgressRequestSeq
    const isCurrent = () => requestId === certificationProgressRequestSeq

    isLoadingCertificationProgress.value = true
    certificationProgressError.value = ''
    try {
      const data = await adminCenterService.getTransformCertificationProgress()
      if (!isCurrent()) return
      certificationProgressRows.value = Array.isArray(data?.rows) ? data.rows : []
    } catch (error) {
      if (!isCurrent()) return
      certificationProgressRows.value = []
      // The gateway answers { success, error, message }; axios' own message only carries the
      // status code, which tells the admin nothing.
      certificationProgressError.value =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        'Failed to load BCS Transform certification progress.'
    } finally {
      if (isCurrent()) {
        isLoadingCertificationProgress.value = false
        hasLoadedCertificationProgress.value = true
      }
    }
  }

  function resetAll() {
    agencyId.value = null
    isLoading.value = false
    certificationProgressRequestSeq += 1
    certificationProgressRows.value = []
    isLoadingCertificationProgress.value = false
    hasLoadedCertificationProgress.value = false
    certificationProgressError.value = ''
    totalEmployers.value = 0
    unassignedEmployers.value = 0
    totalAccountOwners.value = 0
    activeUsers.value = []
    allActiveUsers.value = []
    pendingUsers.value = []
    totalActive.value = 0
    totalPending.value = 0
    isLoadingMoreActive.value = false
    isLoadingMorePending.value = false
    allEmployers.value = []
    unassignedEmployersList.value = []
    totalAllEmployers.value = 0
    totalUnassignedEmployers.value = 0
    isLoadingEmployers.value = false
    isLoadingMoreAllEmployers.value = false
    isLoadingMoreUnassignedEmployers.value = false
  }

  return {
    agencyId,
    isLoading,
    certificationProgressRows,
    isLoadingCertificationProgress,
    hasLoadedCertificationProgress,
    certificationProgressError,
    loadCertificationProgress,
    isLoadingMoreActive,
    isLoadingMorePending,
    totalEmployers,
    unassignedEmployers,
    totalAccountOwners,
    totalActive,
    totalPending,
    invitesSent,
    pendingInvites,
    activeUsers,
    allActiveUsers,
    pendingUsers,
    hasMoreActive,
    hasMorePending,
    resolveAgencyId,
    loadAll,
    loadMoreActiveUsers,
    loadMorePendingUsers,
    createUser,
    grantAgencyAdmin,
    deactivateUser,
    resendInvite,
    cancelInvite,
    allEmployers,
    unassignedEmployersList,
    totalAllEmployers,
    totalUnassignedEmployers,
    isLoadingEmployers,
    isLoadingMoreAllEmployers,
    isLoadingMoreUnassignedEmployers,
    hasMoreAllEmployers,
    hasMoreUnassignedEmployers,
    loadEmployerAssignments,
    loadMoreAllEmployers,
    loadMoreUnassignedEmployers,
    submitAssignments,
    refreshUsers,
    refreshEmployers,
    resetAll
  }
})
